import React, { useEffect, useState } from 'react';
import Swal from 'sweetalert2';
import { Table, Button, Input, Space, Tag, Tooltip, Modal, Divider, Badge } from 'antd';
import {
  SearchOutlined, EditOutlined, DeleteOutlined, PlusOutlined,
  AppstoreOutlined, EyeOutlined, TagOutlined, ShoppingOutlined,
  DollarOutlined, InboxOutlined, StarOutlined, PercentageOutlined,
  LeftOutlined, RightOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import BASEURL from '../API/Config';

const ProductList = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [viewProduct, setViewProduct] = useState(null);
  const [activeImg, setActiveImg] = useState(0);
  const navigate = useNavigate();
  const token = localStorage.getItem('token');

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [catRes, prodRes] = await Promise.all([
          fetch(`${BASEURL}/pcategory`),
          fetch(`${BASEURL}/Products`),
        ]);
        const catData = await catRes.json();
        const prodData = await prodRes.json();
        setCategories(Array.isArray(catData) ? catData : []);
        setProducts(Array.isArray(prodData.products) ? prodData.products : []);
      } catch { Swal.fire('Error', 'Failed to load products.', 'error'); }
      finally { setLoading(false); }
    };
    fetchData();
  }, []);

  const getCategoryName = (id) => categories.find(c => c._id === id)?.title || 'N/A';

  const openView = (product) => {
    setViewProduct(product);
    setActiveImg(0);
  };

  const handleDelete = (id) => {
    Swal.fire({
      title: 'Delete Product?', text: 'This cannot be undone.', icon: 'warning',
      showCancelButton: true, confirmButtonColor: '#e53935', confirmButtonText: 'Yes, Delete',
    }).then(async (result) => {
      if (result.isConfirmed) {
        const res = await fetch(`${BASEURL}/Products/deleteproduct/${id}`, {
          method: 'DELETE', headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          Swal.fire('Deleted!', 'Product removed.', 'success');
          setProducts(p => p.filter(prod => prod._id !== id));
        }
      }
    });
  };

  const filtered = products.filter(p =>
    p.title.toLowerCase().includes(search.toLowerCase()) ||
    (p.description || '').toLowerCase().includes(search.toLowerCase())
  );

  const discount = (p) => p.CurrentPrice && p.offerPrice
    ? Math.round((1 - p.offerPrice / p.CurrentPrice) * 100) : 0;

  const columns = [
    { title: '#', key: 'idx', width: 55, render: (_, __, i) => <span style={{ color: '#6b7280' }}>{i + 1}</span> },
    {
      title: 'PRODUCT', key: 'product',
      render: (r) => (
        <Space>
          {r.images?.[0]?.url ? (
            <img src={`${BASEURL}${r.images[0].url}`} alt="product"
              style={{ width: 42, height: 42, objectFit: 'cover', borderRadius: 8, border: '1px solid #e0e0e0' }} />
          ) : (
            <div style={{ width: 42, height: 42, borderRadius: 8, background: '#f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9ca3af' }}>
              <AppstoreOutlined />
            </div>
          )}
          <div>
            <div style={{ fontWeight: 600, color: '#212121', fontSize: 13, maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.title}</div>
            <div style={{ fontSize: 12, color: '#6b7280' }}>{getCategoryName(r.Category)}</div>
          </div>
        </Space>
      ),
    },
    {
      title: 'PRICE', key: 'price',
      render: (r) => (
        <div>
          <div style={{ fontWeight: 700, color: '#212121', fontSize: 14 }}>₹{r.offerPrice || r.CurrentPrice}</div>
          {r.offerPrice && r.CurrentPrice && r.offerPrice !== r.CurrentPrice && (
            <div style={{ fontSize: 12, color: '#9ca3af', textDecoration: 'line-through' }}>₹{r.CurrentPrice}</div>
          )}
        </div>
      ),
    },
    {
      title: 'QTY / UNIT', key: 'qty',
      render: (r) => <Tag color="blue" style={{ borderRadius: 50 }}>{r.quantity} {r.unit}</Tag>,
    },
    {
      title: 'ACTIONS', key: 'actions', width: 130,
      render: (r) => (
        <Space>
          <Tooltip title="View Details">
            <Button size="small" icon={<EyeOutlined />} onClick={() => openView(r)}
              style={{ color: '#059669', borderColor: '#059669', borderRadius: 4 }} />
          </Tooltip>
          <Tooltip title="Edit">
            <Button size="small" icon={<EditOutlined />} onClick={() => navigate('/admin/add-product', { state: { product: r, isEdit: true } })}
              style={{ color: '#2874f0', borderColor: '#2874f0', borderRadius: 4 }} />
          </Tooltip>
          <Tooltip title="Delete">
            <Button size="small" danger icon={<DeleteOutlined />} onClick={() => handleDelete(r._id)}
              style={{ borderRadius: 4 }} />
          </Tooltip>
        </Space>
      ),
    },
  ];

  /* ─── View Detail Modal Images ─── */
  const images = viewProduct?.images || [];
  const prevImg = () => setActiveImg(i => (i - 1 + images.length) % images.length);
  const nextImg = () => setActiveImg(i => (i + 1) % images.length);

  return (
    <div>
      {/* Page Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: 1 }}>Catalog</div>
          <h2 style={{ margin: 0, fontFamily: 'Outfit,sans-serif', fontSize: 22, fontWeight: 700, color: '#212121' }}>Product List</h2>
        </div>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/admin/add-product')}
          style={{ background: '#2874f0', borderColor: '#2874f0', borderRadius: 4, height: 38, fontWeight: 600 }}>
          Add Product
        </Button>
      </div>

      {/* Table Card */}
      <div style={{ background: '#fff', borderRadius: 8, border: '1px solid #e8ecf0', overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.05)' }}>
        <div style={{ padding: '14px 20px', borderBottom: '1px solid #f3f4f6', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <Input prefix={<SearchOutlined style={{ color: '#9ca3af' }} />} placeholder="Search product..."
            value={search} onChange={(e) => setSearch(e.target.value)} style={{ width: 280, borderRadius: 4 }} />
          <div style={{ marginLeft: 'auto', fontSize: 13, color: '#6b7280' }}>
            Total: <strong style={{ color: '#212121' }}>{filtered.length}</strong> products
          </div>
        </div>
        <Table columns={columns} dataSource={filtered} rowKey="_id" loading={loading}
          pagination={{ pageSize: 10, showTotal: (t) => `${t} products` }} size="middle" />
      </div>

      {/* ─── Product View Details Modal ─── */}
      <Modal
        open={!!viewProduct}
        onCancel={() => setViewProduct(null)}
        footer={null}
        width={800}
        centered
        destroyOnClose
        className="product-view-modal"
        title={null}
        styles={{ body: { padding: 0 } }}
      >
        {viewProduct && (
          <div style={{ display: 'flex', gap: 0, minHeight: 480, borderRadius: 8, overflow: 'hidden' }}>

            {/* ── Left: Image Gallery ── */}
            <div style={{ width: 320, flexShrink: 0, background: '#f8f9fa', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 24, position: 'relative', borderRight: '1px solid #f0f0f0' }}>
              {/* Discount badge */}
              {discount(viewProduct) > 0 && (
                <div style={{ position: 'absolute', top: 16, left: 16, background: '#e53935', color: '#fff', borderRadius: 4, padding: '3px 8px', fontSize: 12, fontWeight: 700, zIndex: 1 }}>
                  {discount(viewProduct)}% OFF
                </div>
              )}

              {/* Main image */}
              <div style={{ width: 240, height: 240, display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
                {images.length > 0 ? (
                  <>
                    <img
                      src={`${BASEURL}${images[activeImg]?.url}`}
                      alt="product"
                      style={{ width: '100%', height: '100%', objectFit: 'contain', borderRadius: 8 }}
                    />
                    {images.length > 1 && (
                      <>
                        <button onClick={prevImg} style={{ position: 'absolute', left: -12, top: '50%', transform: 'translateY(-50%)', background: '#fff', border: '1px solid #e0e0e0', borderRadius: '50%', width: 28, height: 28, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 6px rgba(0,0,0,0.1)', fontSize: 12, color: '#374151' }}>
                          <LeftOutlined />
                        </button>
                        <button onClick={nextImg} style={{ position: 'absolute', right: -12, top: '50%', transform: 'translateY(-50%)', background: '#fff', border: '1px solid #e0e0e0', borderRadius: '50%', width: 28, height: 28, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 6px rgba(0,0,0,0.1)', fontSize: 12, color: '#374151' }}>
                          <RightOutlined />
                        </button>
                      </>
                    )}
                  </>
                ) : (
                  <div style={{ width: 200, height: 200, background: '#e5e7eb', borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9ca3af', fontSize: 48 }}>
                    <InboxOutlined />
                  </div>
                )}
              </div>

              {/* Thumbnail strip */}
              {images.length > 1 && (
                <div style={{ display: 'flex', gap: 8, marginTop: 16, flexWrap: 'wrap', justifyContent: 'center' }}>
                  {images.map((img, i) => (
                    <div key={i} onClick={() => setActiveImg(i)}
                      style={{ width: 52, height: 52, borderRadius: 6, overflow: 'hidden', cursor: 'pointer', border: i === activeImg ? '2px solid #2874f0' : '2px solid #e0e0e0', transition: 'border-color 0.2s' }}>
                      <img src={`${BASEURL}${img.url}`} alt={`thumb-${i}`}
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    </div>
                  ))}
                </div>
              )}

              {/* Image count */}
              {images.length > 0 && (
                <div style={{ marginTop: 12, fontSize: 12, color: '#9ca3af' }}>
                  {activeImg + 1} / {images.length} photos
                </div>
              )}
            </div>

            {/* ── Right: Product Details ── */}
            <div style={{ flex: 1, padding: '28px 28px 24px', overflowY: 'auto', maxHeight: 560 }}>
              {/* Category chip */}
              <div style={{ marginBottom: 8 }}>
                <Tag color="blue" icon={<TagOutlined />} style={{ borderRadius: 50, fontWeight: 600, fontSize: 12 }}>
                  {getCategoryName(viewProduct.Category)}
                </Tag>
              </div>

              {/* Title */}
              <h2 style={{ fontFamily: 'Outfit,sans-serif', fontSize: 20, fontWeight: 700, color: '#212121', margin: '0 0 16px', lineHeight: 1.3 }}>
                {viewProduct.title}
              </h2>

              {/* Price block */}
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, marginBottom: 16, padding: '12px 16px', background: '#f8f9fa', borderRadius: 8, border: '1px solid #f0f0f0' }}>
                <span style={{ fontSize: 28, fontWeight: 800, color: '#212121', fontFamily: 'Outfit,sans-serif' }}>
                  ₹{viewProduct.offerPrice || viewProduct.CurrentPrice}
                </span>
                {viewProduct.offerPrice && viewProduct.CurrentPrice && viewProduct.offerPrice !== viewProduct.CurrentPrice && (
                  <>
                    <span style={{ fontSize: 16, color: '#9ca3af', textDecoration: 'line-through' }}>
                      ₹{viewProduct.CurrentPrice}
                    </span>
                    <span style={{ fontSize: 14, fontWeight: 700, color: '#e53935' }}>
                      {discount(viewProduct)}% off
                    </span>
                  </>
                )}
              </div>

              <Divider style={{ margin: '0 0 16px' }} />

              {/* Info grid */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 20px', marginBottom: 16 }}>
                {[
                  { icon: <InboxOutlined />, label: 'Quantity', value: viewProduct.quantity, color: '#2874f0', bg: '#e8f1ff' },
                  { icon: <ShoppingOutlined />, label: 'Unit', value: viewProduct.unit || '—', color: '#059669', bg: '#ecfdf5' },
                  { icon: <DollarOutlined />, label: 'MRP', value: viewProduct.CurrentPrice ? `₹${viewProduct.CurrentPrice}` : '—', color: '#d97706', bg: '#fffbeb' },
                  { icon: <PercentageOutlined />, label: 'Offer Price', value: viewProduct.offerPrice ? `₹${viewProduct.offerPrice}` : '—', color: '#e53935', bg: '#fef2f2' },
                ].map(({ icon, label, value, color, bg }) => (
                  <div key={label} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', padding: '10px 14px', background: bg, borderRadius: 8 }}>
                    <div style={{ color, fontSize: 16, marginTop: 1 }}>{icon}</div>
                    <div>
                      <div style={{ fontSize: 11, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</div>
                      <div style={{ fontWeight: 700, color: '#212121', fontSize: 14 }}>{value}</div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Description */}
              {viewProduct.description && (
                <>
                  <div style={{ fontWeight: 600, color: '#374151', marginBottom: 8, fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}>
                    Description
                  </div>
                  <div style={{ fontSize: 13.5, color: '#4b5563', lineHeight: 1.7, padding: '12px 14px', background: '#f9fafb', borderRadius: 8, border: '1px solid #f0f0f0', marginBottom: 16 }}>
                    {viewProduct.description}
                  </div>
                </>
              )}

              {/* Tags / Highlights */}
              {viewProduct.tags?.length > 0 && (
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontWeight: 600, color: '#374151', marginBottom: 8, fontSize: 13 }}>Tags</div>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {viewProduct.tags.map((tag, i) => (
                      <Tag key={i} icon={<StarOutlined />} color="geekblue" style={{ borderRadius: 50 }}>{tag}</Tag>
                    ))}
                  </div>
                </div>
              )}

              {/* Footer actions */}
              <Divider style={{ margin: '16px 0 16px' }} />
              <div style={{ display: 'flex', gap: 10 }}>
                <Button icon={<EditOutlined />} type="primary"
                  onClick={() => { setViewProduct(null); navigate('/admin/add-product', { state: { product: viewProduct, isEdit: true } }); }}
                  style={{ background: '#2874f0', borderColor: '#2874f0', borderRadius: 4, fontWeight: 600 }}>
                  Edit Product
                </Button>
                <Button danger icon={<DeleteOutlined />} onClick={() => { setViewProduct(null); handleDelete(viewProduct._id); }}
                  style={{ borderRadius: 4, fontWeight: 600 }}>
                  Delete
                </Button>
                <Button onClick={() => setViewProduct(null)} style={{ marginLeft: 'auto', borderRadius: 4 }}>
                  Close
                </Button>
              </div>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default ProductList;
